import { Injectable, OnDestroy } from "@angular/core";
import { IMqttServiceOptions, IPublishOptions, MqttService } from "ngx-mqtt";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { IClientSubscribeOptions } from "mqtt-browser";
import { StoreService } from "./store.service";
import { ToastrService } from "ngx-toastr";

@Injectable({
    providedIn: "root",
})
export class MqttEventService implements OnDestroy {
    /* ================= STATE ================= */
    private isConnected = false;
    private isLeader = false;

    private clientId = "";
    private deviceId!: string;
    private tabId!: string;

    private heartbeatTimer: any;

    private subscribedTopics = new Set<string>();
    private subscriptions: Subscription[] = [];

    customerInfo: any = {};

    /* ================= STORAGE KEYS ================= */
    private readonly DEVICE_ID = "MQTT_DEVICE_ID";
    public readonly TAB_ID_KEY = "MQTT_TAB_ID";
    public readonly LEADER_KEY = "MQTT_LEADER";
    private readonly HEARTBEAT_KEY = "MQTT_LEADER_HEARTBEAT";

    /* ================= MQTT CONFIG ================= */
    private readonly connection: IMqttServiceOptions = {
        protocol: environment.mqttConfig.protocol as "ws" | "wss",
        hostname: environment.mqttConfig.hostname,
        port: environment.mqttConfig.port,
        path: environment.mqttConfig.path,
        clean: true,
        connectTimeout: 4000,
        reconnectPeriod: 4000,
    };

    constructor(
        private mqttService: MqttService,
        private storeService: StoreService,
        private toastr: ToastrService
    ) {
        this.initTabAndDevice();
        this.registerMqttEvents();
        this.loadCustomer();
        this.watchLeader();
        this.cleanupOnClose();
    }

    /* =====================================================
       PUBLIC API
       ===================================================== */

    createConnection() {
        this.loadCustomer();
        this.tryBecomeLeader();
    }

    disconnect() {
        if (!this.isConnected) return;

        try {
            this.resetSubscriptions();
            this.mqttService.disconnect(true);
        } catch (err) {
            console.error("MQTT disconnect error", err);
        }
    }

    doPublish(topic: string, payload: any) {
        if (!this.isLeader || !this.isConnected) return;

        this.mqttService.unsafePublish(topic, JSON.stringify(payload), {} as IPublishOptions);
    }

    /* =====================================================
       INIT HELPERS
       ===================================================== */

    private initTabAndDevice() {
        // TAB ID (per browser tab)
        const tab = sessionStorage.getItem(this.TAB_ID_KEY);
        this.tabId = tab ?? crypto.randomUUID();
        sessionStorage.setItem(this.TAB_ID_KEY, this.tabId);

        // DEVICE ID (per browser/device)
        const device = localStorage.getItem(this.DEVICE_ID);
        this.deviceId = device ?? crypto.randomUUID();
        localStorage.setItem(this.DEVICE_ID, this.deviceId);

        // console.log("MQTT TAB:", this.tabId);
        // console.log("MQTT DEVICE:", this.deviceId);
    }

    private loadCustomer() {
        const raw = this.storeService.get("customerDetails");
        this.customerInfo = raw ? JSON.parse(raw) : {};
    }

    /* =====================================================
       MQTT EVENTS
       ===================================================== */

    private registerMqttEvents() {
        this.mqttService.onConnect.subscribe(() => {
            // console.log("MQTT connected");
            this.isConnected = true;
            this.subscribeAll();
        });

        this.mqttService.onClose.subscribe(() => {
            // console.log("MQTT disconnected");
            this.isConnected = false;
            this.resetSubscriptions();
        });

        this.mqttService.onReconnect.subscribe(() => {
            // console.log("MQTT reconnecting...");
        });

        this.mqttService.onError.subscribe((err) => {
            console.error("MQTT error", err);
        });
    }

    /* =====================================================
       LEADER ELECTION
       ===================================================== */

    private tryBecomeLeader() {
        const rawLeader = localStorage.getItem(this.LEADER_KEY);
        const heartbeat = Number(localStorage.getItem(this.HEARTBEAT_KEY));

        const leader = rawLeader ? JSON.parse(rawLeader) : null;
        const leaderDead = !heartbeat || Date.now() - heartbeat > 8000;

        // Conditions to become leader
        if (!leader || leader.tabId === this.tabId) {
            this.becomeLeader();
            return;
        }

        // console.log("MQTT handled by another tab");
    }

    private becomeLeader() {
        if (this.isLeader) return;

        // console.log("This tab is MQTT leader");

        this.isLeader = true;

        localStorage.setItem(
            this.LEADER_KEY,
            JSON.stringify({
                tabId: this.tabId,
                deviceId: this.deviceId,
            })
        );

        this.startHeartbeat();
        this.connectMqtt();
    }

    private startHeartbeat() {
        clearInterval(this.heartbeatTimer);

        this.heartbeatTimer = setInterval(() => {
            if (this.isLeader) {
                localStorage.setItem(this.HEARTBEAT_KEY, Date.now().toString());
            }
        }, 3000);
    }

    private watchLeader() {
        window.addEventListener("storage", () => {
            if (!this.isLeader) {
                this.tryBecomeLeader();
            }
        });
    }

    private cleanupOnClose() {
        window.addEventListener("beforeunload", () => {
            if (!this.isLeader) return;

            clearInterval(this.heartbeatTimer);
            localStorage.removeItem(this.LEADER_KEY);
            localStorage.removeItem(this.HEARTBEAT_KEY);
            this.disconnect();
        });
    }

    /* =====================================================
       MQTT CORE
       ===================================================== */

    private connectMqtt() {
        if (this.isConnected) return;

        this.clientId = environment.mqttConfig.clientId + this.customerInfo.customerId + "_" + this.deviceId;

        // console.log("Connecting MQTT:", this.clientId);

        this.mqttService.connect({
            clientId: this.clientId,
            username: this.customerInfo.customerName || "",
            ...this.connection,
        });
    }

    /* =====================================================
       SUBSCRIPTIONS
       ===================================================== */

    private subscribeAll() {
        if (!this.customerInfo?.customerId) return;

        this.subscribeTopic(environment.mqttTopic.admin_topic + this.customerInfo.customerId);

        this.subscribeTopic(environment.mqttTopic.common_topic);
    }

    private subscribeTopic(topic: string) {
        if (this.subscribedTopics.has(topic)) return;

        const sub = this.mqttService.observe(topic, {} as IClientSubscribeOptions).subscribe((message) => {
            // console.log("MQTT:", topic, message);
        });

        this.subscribedTopics.add(topic);
        this.subscriptions.push(sub);
    }

    private resetSubscriptions() {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions = [];
        this.subscribedTopics.clear();
    }

    /* =====================================================
       CLEANUP
       ===================================================== */

    ngOnDestroy() {
        this.disconnect();
        clearInterval(this.heartbeatTimer);
    }
}
